// The staff app's frame (CHANGE-070): the rail, ⌘K, g-then-letter keys, toasts and confirms. The Shell renders this for a
// signed-in staff member on any /staff path, so sign-in and No access stay where they were (app/Shell.tsx).
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { api, useMe, useRefreshMe, useSelectCompany, useSelectPartner } from '../../../lib'
import { setSide } from '../../../app/side'
import { ViewAsBar } from '../ViewAs'
import { KitProvider, when } from '../kit'
import '../kit/kit.css'
import { NAV, NAV_ITEMS } from './nav'
import { PaletteProvider, usePalette, usePaletteActions } from './Palette'

type Counts = Partial<Record<'today' | 'send' | 'card' | 'payouts' | 'jobs', number>> & { me?: string | null }

export function StaffApp() {
  return (
    <KitProvider>
      <PaletteProvider>
        <Frame />
      </PaletteProvider>
    </KitProvider>
  )
}

function Frame() {
  const { me } = useMe()
  const refresh = useRefreshMe()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const palette = usePalette()
  const selectCompany = useSelectCompany()
  const selectPartner = useSelectPartner()
  const [menu, setMenu] = useState(false)
  const [help, setHelp] = useState(false)
  useEffect(() => { setMenu(false); window.scrollTo(0, 0) }, [pathname])
  usePaletteActions('global', [{ id: 'stop', label: 'Ask to stop', hint: 'Asks first', run: () => navigate('/staff/system?stop=1') }, { id: 'keys', label: 'Keyboard shortcuts', hint: '?', run: () => setHelp(true) }])
  const signOut = async () => {
    try { await api('POST', '/auth/logout', {}) } finally { await refresh(); navigate('/') }
  }

  const counts = useQuery({ queryKey: ['staff', 'counts'], queryFn: () => api<Counts>('GET', '/api/staff/counts'), refetchInterval: 60_000 })

  // g then a letter jumps to a page.
  const pending = useRef(0)
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || e.metaKey || e.ctrlKey || e.altKey || document.querySelector('.st-scrim')) return
      if (Date.now() - pending.current < 1000) {
        // The key after g never reaches the page: a typo here must not mark a send.
        pending.current = 0
        e.preventDefault()
        e.stopPropagation()
        const hit = NAV_ITEMS.find((n) => n.key === e.key)
        if (hit) navigate(hit.to)
        return
      }
      if (e.key === 'g') pending.current = Date.now()
      else if (e.key === '?') setHelp(true)
    }
    document.addEventListener('keydown', fn, true)
    return () => document.removeEventListener('keydown', fn, true)
  }, [navigate])

  return (
    <div className="st" data-testid="staff-app">
      {me?.view_as ? <div className="st-viewas"><ViewAsBar /></div> : null}
      <div className="st-top">
        <Link to="/staff" className="st-brand">ernd</Link>
        <span className="st-row">
          <button type="button" onClick={palette.open} data-testid="st-top-search">Find</button>
          <button type="button" onClick={() => setMenu((m) => !m)} aria-expanded={menu} data-testid="st-menu">Menu</button>
        </span>
      </div>
      <aside className={`st-rail${menu ? ' open' : ''}`} aria-label="Staff">
        <div className="st-rail-top"><Link to="/staff" className="st-brand" data-testid="web-brand">ernd</Link></div>
        <button type="button" className="st-search" onClick={palette.open} data-testid="st-search">Find anything <kbd>⌘K</kbd></button>
        {NAV.map((g) => (
          <nav key={g.name ?? 'top'} className="st-group" aria-label={g.name ?? 'Today'}>
            {g.name ? <div className="st-group-name">{g.name}</div> : null}
            {g.items.map((n) => {
              const c = n.count ? counts.data?.[n.count as Exclude<keyof Counts, 'me'>] : undefined
              return (
                <NavLink key={n.to} to={n.to === '/staff/send' && counts.data?.me ? `${n.to}?mine=${counts.data.me}` : n.to} end={n.end} className={({ isActive }) => `st-nav${isActive ? ' active' : ''}`} data-testid={`st-nav-${n.to.replace(/^\/staff\/?/, '').replace(/\//g, '-') || 'today'}`}>
                  <span>{n.label}</span>
                  {c ? <span className={`st-nav-n${n.count === 'jobs' ? ' bad' : ''}`} title={counts.dataUpdatedAt ? `Read ${when(new Date(counts.dataUpdatedAt))}` : undefined} data-testid={`st-count-${n.count}`}>{c}</span> : null}
                </NavLink>
              )
            })}
          </nav>
        ))}
        <div className="st-rail-foot">
          {me?.companies.map((c) => (
            <button key={c.id} type="button" data-testid={`web-side-company-${c.id}`} onClick={() => { selectCompany(c.id); setSide('company'); navigate(me.features?.card_only_app ? '/card' : '/') }}>{c.name}</button>
          ))}
          {me?.partners.map((p) => (
            <button key={p.id} type="button" data-testid={`web-side-partner-${p.id}`} onClick={() => { selectPartner(p.id); setSide('partner'); navigate('/partner') }}>{p.name}</button>
          ))}
          <span title={me?.user?.email}>{me?.user?.email}</span>
          <button type="button" onClick={signOut} data-testid="st-sign-out">Sign out</button>
        </div>
      </aside>
      <main className="st-main"><Outlet /></main>
      {help ? <Keys close={() => setHelp(false)} /> : null}
    </div>
  )
}

const PAGE_KEYS: [string, string][] = [['⌘K or /', 'Find anything'], ['?', 'This list'], ['j k', 'Move'], ['Enter', 'Open'], ['Esc', 'Close']]
const LIST_KEYS: [string, string][] = [['s', 'Change stage on a pipeline, skip on Send'], ['o', 'Set owner on a pipeline, open LinkedIn on Send'], ['c', 'Copy the link on a pipeline, the message on Send'], ['n', 'Note'], ['x', 'Select'], ['d', 'Mark sent'], ['l', 'Copy link on Send'], ['u', 'Undo'], ['a r h', 'Approve, reject, hold a payout']]

function Keys({ close }: { close: () => void }) {
  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [close])
  const Rows = ({ rows }: { rows: [string, string][] }) => <dl className="st-facts">{rows.map(([k, v]) => <div key={k} className="st-contents"><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
  return (
    <div className="st-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) close() }}>
      <div className="st-dialog st-dialog-wide" role="dialog" aria-modal="true" aria-label="Keyboard shortcuts" data-testid="st-keys">
        <h2>Keyboard shortcuts</h2>
        <Rows rows={PAGE_KEYS} />
        <Rows rows={LIST_KEYS} />
        <Rows rows={NAV_ITEMS.filter((n) => n.key).map((n) => [`g ${n.key}`, n.label])} />
      </div>
    </div>
  )
}
